import { CatalogItemSimplified } from "common/types/catalog-types";
import { SearchRequestContent, SearchResponseContent } from "common/types/search-types";
import { API_BASE_URL } from "config";
import { StatusCodes } from "http-status-codes";
import { isNumber } from "lodash";

import { CatalogicalItem } from "../../common/types/catalogical-type";
import { decodeHtmlEntityDeep } from "../helpers/htmlEntityDecode-helper";
import { BaseService } from "./base-service";
import { GetItemsResponse } from "./catalogical-models";
import { ISearchService } from "./i-search-service";

export type SearchExperienceDomains = "software" | "aiforme" | "itshopfaq" | "userusecase";

export interface CatalogicalSearchRequestContent {
  limit?: number | undefined;
  offset?: number | undefined;
  sortBy?: string | undefined;
  sortDirection?: string | undefined;
  fuzzy?: boolean | undefined;
  keywords?: string | undefined;
  filter?: string | undefined;
  itemType?: string;
  useSemanticSearch?: boolean;
  useIntent?: boolean;
  searchQuery?: string;
  excludeFields?: string;
  simplifiedResults?: boolean;
  bypass?: boolean;
  caseInsensitive?: boolean;
  vectorField?: string;
  mode?: string;
}
export class SearchExperienceService extends BaseService implements ISearchService {
  private domain: string;
  private atnRegex: RegExp;

  constructor(domain: SearchExperienceDomains) {
    // Will call IT Shop Search Lambda as proxy
    super(API_BASE_URL);

    this.domain = domain;
    this.atnRegex = new RegExp(`^atn::${this.domain}/`);
  }

  public async getItems<T>(parameters: CatalogicalSearchRequestContent): Promise<GetItemsResponse<T>> {
    const query: Record<string, string> = {};
    for (const [name, value] of Object.entries(parameters) as Array<[string, unknown]>) {
      if (value !== undefined) {
        query[name] = String(value);
      }
    }
    const queryParams = new URLSearchParams(query);

    const url = `/search/${this.domain}?${queryParams.toString()}`;
    const response = await this.fetch(url);
    if ([StatusCodes.OK, StatusCodes.CREATED].includes(response.status)) {
      const itemsResponse = await response.json();
      // Decode HTML entities in the entire response object
      return decodeHtmlEntityDeep(itemsResponse);
    }

    throw new Error("Failed to fetch catalog items");
  }

  public async Search(parameters: SearchRequestContent): Promise<SearchResponseContent> {
    const filters = {
      categories: [...(parameters?.categories ?? []), ...(parameters?.tags ?? [])],
    };

    let currentPage = 0;
    if (isNumber(parameters.nextToken)) {
      currentPage = parseInt(parameters.nextToken);
    }

    const params: CatalogicalSearchRequestContent = {
      limit: parameters.pageSize ?? 10,
      offset: currentPage * (parameters.pageSize ?? 10),
      sortBy: parameters.sort ?? undefined,
      sortDirection: "asc",
      fuzzy: false,
      keywords: parameters.text,
      filter: JSON.stringify(filters),
      useSemanticSearch: false,
      itemType: "software",
      searchQuery: parameters.text,
      //excludeFields?: string,
      simplifiedResults: true,
      bypass: true,
    };

    const response = await this.getItems<CatalogicalItem>(params);

    const nextPage = Math.floor(response.pagination.offset / response.pagination.limit);
    const searchResponse: SearchResponseContent = {
      items: response.data
        ? response.data.map(
            (i) =>
              ({
                ...i,
                logoImageUrl: i.logoUrl,
                itemId: i.taxonomyId,
                price: i.price?.amount.toString() ?? "",
              } as unknown as CatalogItemSimplified)
          )
        : [],
      facets: undefined,
      search: parameters,
      totalResults: response.pagination.total,
      nextToken: nextPage.toString(),
    };
    return searchResponse;
  }
}
